import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const FSSAI = () => {
  const data = {
    title: 'FSSAI License Registration',
    description: 'Get your Food Safety and Standards Authority of India (FSSAI) license for food business. Mandatory for all food manufacturers, processors, distributors, and retailers.',
    keywords: 'FSSAI license, food license India, food safety license, FSSAI registration, food business license, food manufacturing license',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          FSSAI License is a mandatory food safety certification issued by the Food Safety and Standards Authority of India 
          under the Food Safety and Standards Act, 2006. Every food business operator (FBO) in India – whether manufacturer, 
          processor, distributor, retailer, or even home-based food seller – must obtain an FSSAI license before commencing operations.
        </p>
        <p>
          <strong>Why is FSSAI License Important?</strong> The 14-digit FSSAI license number must be displayed on all food 
          products and at business premises. It assures consumers about food safety standards, helps in legal compliance, 
          and is mandatory for selling on e-commerce platforms like Swiggy, Zomato, Amazon, and Flipkart. Operating without 
          FSSAI license can attract penalties up to ₹5 lakhs.
        </p>
        <p>
          <strong>Types of FSSAI License:</strong> Based on the nature and scale of your food business, you need to obtain 
          one of three types: Basic Registration (for petty food businesses with turnover up to ₹12 lakhs), State License 
          (for medium-sized businesses with turnover ₹12 lakhs to ₹20 crores), or Central License (for large businesses, 
          importers/exporters, or multi-state operations with turnover above ₹20 crores).
        </p>
        <p>
          <strong>Food Categories Covered:</strong> All types of food businesses require FSSAI – including restaurants, 
          caterers, food trucks, cloud kitchens, bakeries, confectioneries, dairy products, packaged foods, food manufacturers, 
          wholesalers, importers, and even home-based food businesses like tiffin services and home bakers.
        </p>
      </div>
    ),
    
    benefits: [
      'Legal compliance – avoid penalties up to ₹5 lakhs for operating without license',
      'Mandatory for selling on food delivery apps (Swiggy, Zomato, etc.)',
      'Required for food e-commerce (Amazon, Flipkart, BigBasket)',
      'Builds customer trust and credibility with official FSSAI logo',
      'Essential for food exports and imports',
      'Required for government tenders and institutional supplies',
      'Helps in business expansion and franchise opportunities',
      'Insurance companies may require FSSAI for coverage',
      'Banks may require for food business loans',
      'Necessary for food product labeling compliance',
      'Enables participation in food exhibitions and trade fairs',
      'Protects consumers and ensures food safety standards',
    ],
    
    documents: [
      'PAN Card of business/proprietor/partners/directors',
      'Aadhaar Card or ID proof of proprietor/partners/directors',
      'Passport size photograph of the authorized signatory',
      'Business entity proof (incorporation certificate/partnership deed/GST)',
      'Proof of business premises: Rent agreement or Ownership documents',
      'List of food products to be manufactured/sold with category',
      'Food safety management plan (FSMS) – for manufacturing units',
      'NOC from local municipality (for manufacturing)',
      'Water test report from recognized lab (for manufacturing)',
      'List of equipment and machinery (for manufacturing)',
      'Layout plan of processing area (for manufacturing)',
      'Source of raw materials and suppliers list',
    ],
    
    process: [
      'Determine the type of license needed based on turnover and business nature',
      'Document collection and verification as per license type',
      'Online application on FSSAI FoSCoS portal',
      'Payment of applicable license fee',
      'Document scrutiny by FSSAI authority',
      'Physical inspection of premises (for State and Central license)',
      'Verification by Food Safety Officer (if required)',
      'License approval and issuance',
      'Display FSSAI logo and license number at premises',
      'Annual compliance and renewal before expiry',
    ],
    
    timeline: '7-15 Working Days',
    pricing: '₹3,499 (Basic), ₹5,999 (State), ₹9,999 (Central)',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Government Fees Structure:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Basic Registration: ₹100 per year (1-5 years validity)</li>
          <li>State License: ₹2,000 - ₹5,000 per year based on category</li>
          <li>Central License: ₹7,500 per year</li>
          <li>Late fee: ₹100 per day for delayed renewal</li>
        </ul>
        <p className="mt-3"><strong>License Types by Business:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>Basic:</strong> Petty retailers, hawkers, temporary stalls, home-based businesses (turnover &lt; ₹12 lakhs)</li>
          <li><strong>State:</strong> Restaurants, caterers, medium manufacturers, wholesalers, storage (turnover ₹12L - ₹20Cr)</li>
          <li><strong>Central:</strong> Large manufacturers, importers, exporters, multi-state chains, e-commerce, 100% EOU</li>
        </ul>
        <p className="mt-3"><strong>Validity and Renewal:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>License validity: 1 to 5 years (as selected during application)</li>
          <li>Renewal application: 30 days before expiry</li>
          <li>Late renewal attracts penalty of ₹100/day</li>
          <li>License cancelled if not renewed within grace period</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'What are the types of FSSAI licenses?',
        answer: 'There are three types: Basic Registration (turnover up to ₹12 lakhs, for petty businesses), State License (turnover ₹12 lakhs to ₹20 crores, for medium businesses), and Central License (above ₹20 crores, importers/exporters, multi-state operations).',
      },
      {
        question: 'Is FSSAI license mandatory for home-based food business?',
        answer: 'Yes, even home-based food businesses like tiffin services, home bakers, pickle makers need FSSAI registration. Basic Registration is sufficient for most home businesses with turnover under ₹12 lakhs.',
      },
      {
        question: 'How long does it take to get FSSAI license?',
        answer: 'Basic Registration: 7 days, State License: 30-60 days (may require inspection), Central License: 60-90 days. Our professional service expedites the process significantly.',
      },
      {
        question: 'What is the validity of FSSAI license?',
        answer: 'FSSAI license can be obtained for 1 to 5 years. Longer validity periods are cost-effective. Renewal must be applied 30 days before expiry to avoid late fees and business disruption.',
      },
      {
        question: 'Can I operate multiple locations with one license?',
        answer: 'No, each location requires a separate license. However, the head office Central License can cover multiple manufacturing units in different states. Retail outlets, restaurants need individual licenses.',
      },
      {
        question: 'What happens if I operate without FSSAI license?',
        answer: 'Operating without valid FSSAI license attracts penalties up to ₹5 lakhs. Additionally, food articles can be seized, and business can be shut down. E-commerce platforms and delivery apps will not onboard you without FSSAI.',
      },
      {
        question: 'Is physical inspection mandatory for FSSAI?',
        answer: 'Basic Registration does not require inspection. State and Central Licenses may require physical inspection by Food Safety Officer, especially for manufacturing units. Inspection is waived for low-risk categories.',
      },
      {
        question: 'Can I modify my FSSAI license later?',
        answer: 'Yes, you can modify license details like adding products, changing address, or upgrading license type. Modification application is filed on FoSCoS portal with required documents and applicable fees.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default FSSAI;
